/**
 * @file 联机会话（onlineSession）—— WebSocket 对接 M3 服务端。
 *
 * 协议要点（见 `@majiang/protocol`）：
 * - 连接建立后先发 `hello`（带 protocolVersion / coreContractVersion / seatToken /
 *   resumeFromSeq），服务端回 `welcome`；`welcome.snapshot` 可空（对局未开始时为 null）；
 * - 日常走 `ev`（事件批）增量，`viewReducer` 应用到本地 `ClientViewState`；
 * - 发现缺口（`action_rejected.serverSeq > view.seq`）或尚无基线时发 `resync` 取全量 `snap`；
 * - 心跳：协议级 `__ping__/__pong__` 由服务端自动应答（浏览器侧无需实现）；
 *   业务 `ping` 每 25s 发一次保活，并做「假死检测」——超过 40s 无下行即主动重连；
 * - 断线重连：`hello` 带 `resumeFromSeq = 已确认的最后 seq`，服务端补增量或推全量。
 */

import type { Action, SeatIndex } from '@majiang/core';
import { CORE_CONTRACT_VERSION } from '@majiang/core';
import type { ClientMessage, RoomMessage, ServerMessage } from '@majiang/protocol';
import { PROTOCOL_VERSION } from '@majiang/protocol';
import type { ClientViewState, OnlineConnInfo, RoomState, SessionCallbacks, SessionController, TableUiState, UIAction } from './types';
import { applyEvents, applySnapshot, createClientState } from './viewReducer';
import { deriveUi, zimoWinningTileId } from './ui';

const HEARTBEAT_MS = 25_000;
const ALIVE_TIMEOUT_MS = 40_000;
const MAX_RECONNECT = 5;

/** 把 UI 动作映射为联机 `act` 载荷（core Action）。 */
function uiToCoreAction(action: UIAction, seat: SeatIndex, expectedSeq: number, state: ClientViewState): Action | null {
  const myClaim = state.myClaim;
  switch (action.type) {
    case 'declare_void':
      return { type: 'declare_void', seat, expectedSeq, suit: action.suit };
    case 'discard':
      return { type: 'discard', seat, expectedSeq, tileId: action.tileId };
    case 'peng': {
      const tileId = myClaim?.tileId;
      if (tileId === undefined) return null;
      return { type: 'peng', seat, expectedSeq, tileId };
    }
    case 'gang':
      return { type: 'gang', seat, expectedSeq, kind: action.kind };
    case 'hu': {
      // 响应窗口胡（点炮/抢杠）：用窗口里的牌；自家回合自摸胡：复算和了张 id
      const tileId = myClaim?.tileId ?? (state.view !== null ? zimoWinningTileId(state.view) : null);
      if (tileId === null) return null;
      return { type: 'hu', seat, expectedSeq, tileId };
    }
    case 'pass':
      return { type: 'pass', seat, expectedSeq };
  }
}

function mapRoom(msg: RoomMessage['d']): RoomState {
  return {
    roomCode: msg.roomCode,
    members: msg.members,
    ownerSeat: msg.ownerSeat,
    started: msg.started,
  };
}

export class OnlineSession implements SessionController {
  readonly mode = 'online' as const;
  get seat(): SeatIndex | null {
    return this.seatInternal;
  }
  private seatInternal: SeatIndex | null = null;
  private readonly conn: OnlineConnInfo;
  private readonly cb: SessionCallbacks;
  private ws: WebSocket | null = null;
  private clientState: ClientViewState = createClientState(null);
  private lastSeq: number | null = null;
  private lastMessageAt = 0;
  private heartbeatTimer: number | null = null;
  private aliveTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private resyncPending = false;
  private disposed = false;

  constructor(conn: OnlineConnInfo, cb: SessionCallbacks) {
    this.conn = conn;
    this.cb = cb;
  }

  async start(): Promise<void> {
    this.connect();
  }

  send(action: UIAction): void {
    const ws = this.ws;
    const view = this.clientState.view;
    if (ws === null || ws.readyState !== WebSocket.OPEN || this.seatInternal === null || view === null) {
      this.cb.onToast('连接尚未就绪');
      return;
    }
    const core = uiToCoreAction(action, this.seatInternal, view.seq, this.clientState);
    if (core === null) {
      this.cb.onToast('当前不能执行该操作');
      return;
    }
    const msg: ClientMessage = { t: 'act', d: core };
    ws.send(JSON.stringify(msg));
  }

  resync(): void {
    if (this.resyncPending) return;
    this.resyncPending = true;
    this.sendRaw({ t: 'resync', d: { reason: 'manual' } });
  }

  dispose(): void {
    this.disposed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    if (this.ws !== null) {
      try {
        this.ws.onclose = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.close(1000, 'client dispose');
      } catch {
        /* 忽略 */
      }
      this.ws = null;
    }
  }

  /* ─────────────── 连接管理 ─────────────── */

  private connect(): void {
    if (this.disposed) return;
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${proto}://${window.location.host}${this.conn.wsPath}`);
    } catch {
      this.cb.onError('无法建立 WebSocket 连接', true);
      return;
    }
    this.ws = ws;
    this.lastMessageAt = Date.now();
    ws.onopen = () => this.onOpen();
    ws.onmessage = (ev) => this.onMessage(ev.data);
    ws.onclose = () => this.onClose();
    ws.onerror = () => {
      // 错误后必然触发 onclose，统一在那里处理
    };
  }

  private onOpen(): void {
    const msg: ClientMessage = {
      t: 'hello',
      d: {
        protocolVersion: PROTOCOL_VERSION,
        coreContractVersion: CORE_CONTRACT_VERSION,
        seatToken: this.conn.seatToken,
        resumeFromSeq: this.lastSeq,
      },
    };
    this.sendRaw(msg);
    this.startHeartbeat();
  }

  private onClose(): void {
    this.stopHeartbeat();
    this.ws = null;
    if (this.disposed) return;
    if (this.reconnectAttempts < MAX_RECONNECT) {
      const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 8000);
      this.reconnectAttempts += 1;
      this.cb.onToast(`连接断开，${Math.round(delay / 1000)}s 后重连…`);
      this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    } else {
      this.cb.onError('无法连接服务器，请返回重试', true);
    }
  }

  private onMessage(raw: unknown): void {
    this.lastMessageAt = Date.now();
    let msg: ServerMessage;
    try {
      msg = JSON.parse(String(raw)) as ServerMessage;
    } catch {
      return;
    }
    switch (msg.t) {
      case 'welcome': {
        const { seat, snapshot } = msg.d;
        this.seatInternal = seat;
        this.reconnectAttempts = 0;
        this.resyncPending = false;
        if (snapshot !== null) {
          this.clientState = applySnapshot(this.clientState, snapshot.view);
          this.lastSeq = snapshot.view.seq;
        }
        this.notify();
        break;
      }
      case 'ev': {
        if (this.clientState.view === null) {
          // 尚无基线（开局事件先于快照到达）：请求全量快照
          this.resync();
          return;
        }
        this.clientState = applyEvents(this.clientState, msg.d.events);
        const lastEv = msg.d.events[msg.d.events.length - 1];
        if (lastEv !== undefined) this.lastSeq = Math.max(this.lastSeq ?? 0, lastEv.seq);
        if (this.clientState.needsResync) {
          this.resync();
          return;
        }
        this.notify();
        // 摸牌动画
        for (const ev of msg.d.events) {
          if (ev.type === 'drawn_self' && ev.payload.seat === this.seatInternal) {
            this.cb.onDraw?.(ev.payload.tileId);
          }
        }
        break;
      }
      case 'snap': {
        this.clientState = applySnapshot(this.clientState, msg.d.view);
        this.lastSeq = msg.d.view.seq;
        this.resyncPending = false;
        this.notify();
        break;
      }
      case 'pong':
        break; // 心跳应答：lastMessageAt 已更新即视为存活
      case 'room':
        this.cb.onRoom(mapRoom(msg.d));
        break;
      case 'err': {
        this.cb.onError(msg.d.message, !msg.d.retryable);
        if (msg.d.retryable && !this.disposed) {
          this.resync();
        }
        break;
      }
    }
  }

  private sendRaw(msg: ClientMessage): void {
    const ws = this.ws;
    if (ws !== null && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }

  /* ─────────────── 心跳与假死检测 ─────────────── */

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendRaw({ t: 'ping', d: { ts: Date.now() } });
      }
    }, HEARTBEAT_MS);
    this.aliveTimer = window.setInterval(() => {
      if (this.ws !== null && Date.now() - this.lastMessageAt > ALIVE_TIMEOUT_MS) {
        // 假死：强制关闭以触发重连
        try {
          this.ws.close();
        } catch {
          /* 忽略 */
        }
      }
    }, 5_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) window.clearInterval(this.heartbeatTimer);
    if (this.aliveTimer !== null) window.clearInterval(this.aliveTimer);
    this.heartbeatTimer = null;
    this.aliveTimer = null;
  }

  /* ─────────────── 通知外壳 ─────────────── */

  private notify(): void {
    const ui: TableUiState = deriveUi(this.clientState);
    this.cb.onView(this.clientState, ui);
  }
}
